/**
 * Workflow Persistence Hook
 * React hook for managing workflow persistence and auto-save functionality
 */

import { useEffect, useState, useCallback } from 'react';
import { useCanvasStore } from '../stores/canvas';
import { workflowPersistenceService, WorkflowSession, PersistenceSettings } from '../services/WorkflowPersistenceService';

export interface UseWorkflowPersistenceReturn {
  // Session management
  currentSession: WorkflowSession | null;
  hasUnsavedChanges: boolean;
  
  // Actions
  saveWorkflow: (name?: string) => Promise<{ success: boolean; id?: string; error?: string }>;
  loadWorkflow: (workflowId: string) => Promise<{ success: boolean; error?: string }>;
  newWorkflow: () => void;
  
  // Recent workflows
  recentWorkflows: WorkflowSession[];
  refreshRecentWorkflows: () => Promise<void>;
  
  // Auto-save settings
  autoSaveEnabled: boolean;
  setAutoSaveEnabled: (enabled: boolean) => void;
  settings: PersistenceSettings;
  updateSettings: (settings: Partial<PersistenceSettings>) => void;
  
  // Status
  isInitialized: boolean;
  isSaving: boolean;
  isLoading: boolean;
  lastSaveTime: Date | null;
  error: string | null;
}

export function useWorkflowPersistence(): UseWorkflowPersistenceReturn {
  // Canvas store integration
  const { 
    exportUniversalWorkflow,
    loadUniversalWorkflow,
    clearWorkflow,
    universalNodes
  } = useCanvasStore();

  // Local state
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState<WorkflowSession | null>(null);
  const [recentWorkflows, setRecentWorkflows] = useState<WorkflowSession[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabledState] = useState(true);
  const [settings, setSettings] = useState<PersistenceSettings>({
    autoSaveInterval: 30,
    maxAutoSaves: 5,
    enableSessionRestore: true,
    showUnsavedWarning: true,
    compressionEnabled: true
  });
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize persistence service
  useEffect(() => {
    const initialize = async () => {
      try {
        await workflowPersistenceService.initialize();
        
        // Get current session
        const session = workflowPersistenceService.getCurrentSession();
        setCurrentSession(session);
        
        // Get settings
        const currentSettings = workflowPersistenceService.getSettings();
        setSettings(currentSettings);
        setAutoSaveEnabledState(session?.autoSaveEnabled ?? true);
        
        // Load recent workflows
        await refreshRecentWorkflows();
        
        setIsInitialized(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize persistence');
        setIsInitialized(true);
      }
    };

    initialize();
  }, []);

  // Monitor workflow changes for auto-save
  useEffect(() => {
    if (!isInitialized) return;

    const updatePersistence = async () => {
      const currentWorkflow = exportUniversalWorkflow();
      
      // Check if workflow has any content
      const hasContent = Object.keys(currentWorkflow.nodes).length > 0;
      
      if (hasContent) {
        await workflowPersistenceService.updateWorkflow(currentWorkflow);
        
        // Update unsaved changes status
        setHasUnsavedChanges(workflowPersistenceService.hasUnsavedChanges());
        
        // Update current session
        setCurrentSession(workflowPersistenceService.getCurrentSession());
      }
    };

    updatePersistence();
  }, [universalNodes, isInitialized, exportUniversalWorkflow]);

  // Refresh recent workflows
  const refreshRecentWorkflows = useCallback(async () => {
    try {
      const recent = await workflowPersistenceService.getRecentWorkflows();
      setRecentWorkflows(recent);
    } catch (err) {
      console.error('Failed to load recent workflows:', err);
    }
  }, []);

  // Save workflow
  const saveWorkflow = useCallback(async (name?: string) => {
    if (isSaving) return { success: false, error: 'Already saving' };
    
    setIsSaving(true);
    setError(null);
    
    try {
      const result = await workflowPersistenceService.saveWorkflow(name);
      
      if (result.success) {
        setHasUnsavedChanges(false);
        setCurrentSession(workflowPersistenceService.getCurrentSession());
        setLastSaveTime(new Date());
        await refreshRecentWorkflows();
      } else {
        setError(result.error || 'Save failed');
      }
      
      return result;
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Save failed';
      setError(error);
      return { success: false, error };
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, refreshRecentWorkflows]);

  // Load workflow
  const loadWorkflow = useCallback(async (workflowId: string) => {
    if (isLoading) return { success: false, error: 'Already loading' };
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await workflowPersistenceService.loadWorkflow(workflowId);
      
      if (result.success && result.workflow) {
        loadUniversalWorkflow(result.workflow);
        setHasUnsavedChanges(false);
        setCurrentSession(workflowPersistenceService.getCurrentSession());
        await refreshRecentWorkflows();
        return { success: true };
      } else {
        setError(result.error || 'Load failed');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Load failed';
      setError(error);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, loadUniversalWorkflow, refreshRecentWorkflows]);

  // New workflow
  const newWorkflow = useCallback(() => {
    clearWorkflow();
    setCurrentSession(null);
    setHasUnsavedChanges(false);
    setLastSaveTime(null);
    setError(null);
  }, [clearWorkflow]);

  // Enable/disable auto-save
  const setAutoSaveEnabled = useCallback((enabled: boolean) => {
    workflowPersistenceService.setAutoSaveEnabled(enabled);
    setAutoSaveEnabledState(enabled);
  }, []);

  // Update settings
  const updateSettings = useCallback((newSettings: Partial<PersistenceSettings>) => {
    workflowPersistenceService.updateSettings(newSettings);
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  // Setup periodic status updates
  useEffect(() => {
    if (!isInitialized) return;

    const interval = setInterval(() => {
      setHasUnsavedChanges(workflowPersistenceService.hasUnsavedChanges());
      setCurrentSession(workflowPersistenceService.getCurrentSession());
    }, 1000); // Check every second

    return () => clearInterval(interval);
  }, [isInitialized]);

  return {
    // Session management
    currentSession,
    hasUnsavedChanges,
    
    // Actions
    saveWorkflow,
    loadWorkflow,
    newWorkflow,
    
    // Recent workflows
    recentWorkflows,
    refreshRecentWorkflows,
    
    // Auto-save settings
    autoSaveEnabled,
    setAutoSaveEnabled,
    settings,
    updateSettings,
    
    // Status
    isInitialized,
    isSaving,
    isLoading,
    lastSaveTime,
    error
  };
}